import React, { useState, useEffect, useMemo } from 'react';
import {
    MdStar, MdStarBorder, MdPerson, MdRateReview,
    MdInfoOutline, MdKeyboardArrowLeft, MdKeyboardArrowRight
} from 'react-icons/md';

import { usersAPI, ratingsAPI } from 'services/api';
import { useAuth } from 'context/AuthContext';

// --- YARDIMCI FONKSİYONLAR ---
const getInitials = (name) => {
    if (!name) return '?';
    const parts = String(name).trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${(parts[0][0] || '')}${(parts[parts.length - 1][0] || '')}`.toUpperCase();
};

// Premium Yıldız Bileşeni (HorizonUI Style)
const StarRating = ({ rating }) => {
    const stars = [];
    const numRating = Number(rating || 0);
    for (let i = 1; i <= 5; i++) {
        if (i <= numRating) {
            stars.push(<MdStar key={i} className="text-amber-500 text-lg" />);
        } else {
            stars.push(<MdStarBorder key={i} className="text-gray-300 dark:text-gray-600 text-lg" />);
        }
    }
    return <div className="flex items-center gap-0.5">{stars}</div>;
};

// --- SKELETON LOADERS ---
const StaffSkeletonGrid = () => (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 animate-pulse">
        {[...Array(5)].map((_, i) => (
            <div key={i} className="h-40 w-full bg-gray-200 dark:bg-navy-700 rounded-2xl" />
        ))}
    </div>
);

const ReviewsSkeletonList = () => (
    <div className="flex flex-col gap-4 animate-pulse mt-4">
        <div className="h-12 w-full bg-gray-200 dark:bg-navy-700 rounded-xl" />
        {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 w-full bg-gray-100 dark:bg-navy-900/50 rounded-2xl" />
        ))}
    </div>
);


const AdminStaffRatings = () => {
    // --- STATE KONTROLLERİ ---
    const [staffList, setStaffList] = useState([]);
    const [allRatings, setAllRatings] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [filteredRatings, setFilteredRatings] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Responsive Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(window.innerWidth < 768 ? 5 : 10);

    const { logout } = useAuth();
    const handleAuthError = () => logout();

    // Ekran boyutu değiştiğinde PageSize'ı güncelle
    useEffect(() => {
        const handleResize = () => setPageSize(window.innerWidth < 768 ? 5 : 10);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // --- İLK YÜKLEME (WORK-LOGIC BİREBİR KORUNDU) ---
    useEffect(() => {
        let mounted = true;
        const fetchInitialData = async () => {
            try {
                setLoading(true);
                setError(null);

                const [staffResponse, ratingsResponse] = await Promise.all([
                    usersAPI.getStaff(),
                    ratingsAPI.getAll(),
                ]);

                if (!staffResponse?.success || !staffResponse?.staff || !ratingsResponse?.success) {
                    throw new Error('Veriler yüklenirken bir hata oluştu.');
                }

                if (mounted) {
                    setStaffList(staffResponse.staff);
                    setAllRatings(ratingsResponse.ratings || []);
                }
            } catch (err) {
                console.error('Initial fetch error:', err);
                if (String(err.message).includes('401') || String(err.message).includes('Unauthorized')) {
                    handleAuthError();
                    return;
                }
                if (mounted) setError(err.message || 'Veriler yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchInitialData();
        return () => { mounted = false; };
    }, []);

    // --- SEÇİLİ PERSONELE GÖRE FİLTRELEME (WORK-LOGIC BİREBİR) ---
    useEffect(() => {
        if (!selectedStaff) {
            setFilteredRatings([]);
            return;
        }

        const ratingsForStaff = (allRatings || [])
            .filter(r => r.staff_id === selectedStaff.id)
            .map(r => ({
                id: r.id,
                customerName: r.customer ? `${r.customer.name} ${r.customer.surname}` : 'Bilinmeyen Müşteri',
                // Orijinal mantıkta UI için TR date kullanılmıştı. Sıralama için raw timestamp ekliyoruz.
                rawDate: new Date(r.created_at).getTime(),
                date: new Date(r.created_at).toLocaleDateString('tr-TR'),
                rating: Number(r.rating || 0),
                comment: r.comment || 'Yorum yapılmamış',
            }))
            // Zaman damgasına göre (en yeni en üstte) kusursuz sıralama
            .sort((a, b) => b.rawDate - a.rawDate);

        setFilteredRatings(ratingsForStaff);
    }, [selectedStaff, allRatings]);

    // Personel değişince sayfayı 1'e al
    useEffect(() => {
        setCurrentPage(1);
    }, [selectedStaff]);

    const handleSelectStaff = (staff) => {
        setSelectedStaff(prev => (prev?.id === staff.id ? null : staff));
    };

    // --- SAYFALAMA HESAPLARI ---
    const totalItems = filteredRatings.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const pageStartIndex = (currentPage - 1) * pageSize;
    const currentPageItems = filteredRatings.slice(pageStartIndex, pageStartIndex + pageSize);

    const goToPage = (p) => {
        if (p < 1 || p > totalPages) return;
        setCurrentPage(p);
    };

    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20 animate-fadeIn">

            {/* ÜST BAŞLIK */}
            <div className="flex items-center gap-4 rounded-[20px] bg-white p-6 shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-500 dark:bg-amber-400/10 dark:text-amber-400">
                    <MdRateReview className="text-3xl" />
                </div>
                <div>
                    <h4 className="text-xl font-bold text-navy-700 dark:text-white">Personel Değerlendirmeleri</h4>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                        Müşteriler tarafından personellere verilen puanları ve yorumları inceleyin.
                    </p>
                </div>
            </div>

            {error && (
                <div className="flex items-center gap-3 rounded-xl bg-red-50 p-4 text-red-600 border border-red-100 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
                    <MdInfoOutline className="text-xl flex-none" />
                    <span className="text-sm font-bold">{error}</span>
                </div>
            )}

            {/* 1. ADIM: PERSONEL SEÇİMİ (KART GRID YAPISI) */}
            <div className="flex flex-col bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10 p-6">
                <h5 className="text-base font-bold text-navy-700 dark:text-white mb-4">1. Personel Seçin</h5>

                {loading ? (
                    <StaffSkeletonGrid />
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {staffList.map((staff) => {
                            const fullName = `${staff.name} ${staff.surname}`.trim();
                            const isSelected = selectedStaff?.id === staff.id;
                            const hasRating = staff.total_ratings > 0;
                            const avgRating = Number(staff.average_rating || 0).toFixed(1);

                            return (
                                <div
                                    key={staff.id}
                                    onClick={() => handleSelectStaff(staff)}
                                    className={`relative flex flex-col items-center justify-center p-5 rounded-[16px] cursor-pointer transition-all duration-200 border-2 ${
                                        isSelected
                                            ? 'bg-brand-50 border-brand-500 dark:bg-brand-400/10 dark:border-brand-400 shadow-md'
                                            : 'bg-gray-50/50 border-transparent hover:border-gray-200 dark:bg-navy-900/50 dark:hover:border-white/10'
                                    }`}
                                >
                                    {/* Seçili Rozeti */}
                                    {isSelected && (
                                        <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-brand-500 shadow-sm" />
                                    )}

                                    {/* Avatar */}
                                    <div className={`flex h-14 w-14 items-center justify-center rounded-full font-bold text-white shadow-md mb-3 ${isSelected ? 'bg-brand-500' : 'bg-navy-300 dark:bg-navy-600'}`}>
                                        {staff.profile_picture ? (
                                            <img src={staff.profile_picture} alt="profil" className="h-full w-full rounded-full object-cover" />
                                        ) : (
                                            getInitials(fullName)
                                        )}
                                    </div>

                                    <h6 className={`text-sm font-bold text-center leading-tight mb-2 ${isSelected ? 'text-brand-600 dark:text-brand-400' : 'text-navy-700 dark:text-white'}`}>
                                        {fullName}
                                    </h6>

                                    {/* N/A Çirkinliği Giderildi - Yıldız Düzeni Getirildi */}
                                    <div className="flex flex-col items-center">
                                        {hasRating ? (
                                            <>
                                                <div className="flex items-center gap-1 font-black text-amber-500 text-lg leading-none">
                                                    {avgRating} <MdStar className="text-xl" />
                                                </div>
                                                <span className="text-[10px] font-bold text-gray-400 uppercase mt-1">({staff.total_ratings} OY)</span>
                                            </>
                                        ) : (
                                            <span className="inline-flex items-center rounded-md bg-gray-200 px-2 py-1 text-[10px] font-bold text-gray-500 dark:bg-navy-700 dark:text-gray-400 uppercase">
                        Henüz Oylanmadı
                      </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* 2. ADIM: DEĞERLENDİRME LİSTESİ (SEÇİLDİYSE GÖRÜNÜR) */}
            {selectedStaff && (
                <div className="flex flex-col bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10 p-6 animate-slideUp">

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-100 dark:border-white/10">
                        <div>
                            <h5 className="text-base font-bold text-navy-700 dark:text-white">
                                2. Değerlendirmeler: <span className="text-brand-500 dark:text-brand-400">{selectedStaff.name} {selectedStaff.surname}</span>
                            </h5>
                        </div>
                        {totalItems > 0 && (
                            <div className="bg-gray-50 dark:bg-navy-900 px-3 py-1 rounded-lg text-sm font-bold text-gray-500 dark:text-gray-400">
                                {pageStartIndex + 1}-{Math.min(pageStartIndex + pageSize, totalItems)} / {totalItems} Gösteriliyor
                            </div>
                        )}
                    </div>

                    {loading ? (
                        <ReviewsSkeletonList />
                    ) : totalItems === 0 ? (
                        /* BOŞ DURUM */
                        <div className="flex flex-col items-center justify-center py-10 opacity-60">
                            <MdRateReview className="text-6xl text-gray-300 dark:text-gray-600 mb-3" />
                            <h6 className="text-lg font-bold text-navy-700 dark:text-white">Değerlendirme Bulunmuyor</h6>
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-1">Bu personel için henüz bir yorum yapılmamış.</p>
                        </div>
                    ) : (
                        <>
                            {/* MASAÜSTÜ TABLO GÖRÜNÜMÜ */}
                            <div className="hidden md:block overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                    <tr className="border-b border-gray-100 dark:border-white/10 text-[11px] font-bold uppercase tracking-widest text-gray-400">
                                        <th className="py-3 pl-4">Müşteri</th>
                                        <th className="py-3">Tarih</th>
                                        <th className="py-3">Puan</th>
                                        <th className="py-3 pr-4 w-1/2">Yorum</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {currentPageItems.map(review => (
                                        <tr key={review.id} className="border-b border-gray-50 dark:border-white/5 hover:bg-gray-50/50 dark:hover:bg-white/5 transition-colors">
                                            <td className="py-4 pl-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-navy-900 dark:text-gray-400 font-bold text-xs">
                                                        {getInitials(review.customerName)}
                                                    </div>
                                                    <span className="text-sm font-bold text-navy-700 dark:text-white">
                              {review.customerName}
                            </span>
                                                </div>
                                            </td>
                                            <td className="py-4 text-sm font-medium text-gray-600 dark:text-gray-400">
                                                {review.date}
                                            </td>
                                            <td className="py-4">
                                                <StarRating rating={review.rating} />
                                            </td>
                                            <td className="py-4 pr-4">
                                                <div className="text-sm font-medium italic text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-navy-900/50 p-3 rounded-xl">
                                                    "{review.comment}"
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* MOBİL KART GÖRÜNÜMÜ */}
                            <div className="grid grid-cols-1 md:hidden gap-4">
                                {currentPageItems.map(review => (
                                    <div key={review.id} className="flex flex-col p-4 rounded-[16px] bg-gray-50 dark:bg-navy-900/50 border border-gray-100 dark:border-white/5">
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white dark:bg-navy-800 text-gray-500 dark:text-gray-300 font-bold shadow-sm">
                                                    {getInitials(review.customerName)}
                                                </div>
                                                <div>
                                                    <h6 className="text-sm font-bold text-navy-700 dark:text-white leading-tight">{review.customerName}</h6>
                                                    <span className="text-xs font-medium text-gray-400">{review.date}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <StarRating rating={review.rating} />
                                        </div>
                                        <div className="text-sm font-medium italic text-gray-600 dark:text-gray-300 bg-white dark:bg-navy-800 p-3 rounded-xl border border-gray-100 dark:border-white/5">
                                            "{review.comment}"
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* SAYFALAMA (PAGINATION) */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-gray-100 dark:border-white/10">
                                    <button
                                        onClick={() => goToPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all"
                                    >
                                        <MdKeyboardArrowLeft className="text-xl" />
                                    </button>

                                    <div className="flex items-center gap-1">
                                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                                            .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                                            .reduce((acc, p, idx, arr) => {
                                                if (idx === 0) return [p];
                                                if (p - arr[idx - 1] > 1) acc.push(-1);
                                                acc.push(p);
                                                return acc;
                                            }, [])
                                            .map((p, idx) =>
                                                p === -1 ? (
                                                    <span key={`sep-${idx}`} className="px-2 text-gray-400">...</span>
                                                ) : (
                                                    <button
                                                        key={p}
                                                        onClick={() => goToPage(p)}
                                                        className={`flex h-9 w-9 items-center justify-center rounded-lg text-sm font-bold transition-all ${
                                                            p === currentPage
                                                                ? 'bg-brand-500 text-white shadow-md'
                                                                : 'bg-transparent text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-navy-700'
                                                        }`}
                                                    >
                                                        {p}
                                                    </button>
                                                )
                                            )
                                        }
                                    </div>

                                    <button
                                        onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all"
                                    >
                                        <MdKeyboardArrowRight className="text-xl" />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Ekstra CSS (Animasyonlar İçin) */}
            <style jsx="true">{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slideUp { animation: slideUp 0.3s ease-out forwards; }
      `}</style>
        </div>
    );
};

export default AdminStaffRatings;